import { ChangeEventHandler, useState } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { connect, DataItem } from 'scrivito'

import { Search } from '@/Atomic/Molecules/Inputs/Search'
import { Sublist, SublistFooter } from '@/Atomic/Templates/Sublist'
import { DataItemDrawer } from '@/Atomic/Widgets/DataItemDrawer'
import { FollowUpSublistItem } from '@/Atomic/Widgets/DataItemTabs/Activity/FollowUpsTab/FollowUpSublistItem'
import { NewFollowUpActivityDrawer } from '@/Components/Activity/NewFollowUpActivityDrawer/NewFollowUpActivityDrawer'
import { Activity } from '@/Data/Activity/ActivityDataClass'
import { DataClassName } from '@/Data/const'
import { SchemaAttributes } from '@/Data/types'
import { useFlashSublistItem } from '@/hooks/useFlashSublistItem'
import { useDataItemDrawer } from '@/utils/CRUD/hooks/useDataItemDrawer'
import { dataScopeHasMore } from '@/utils/dataScopeHasMore'
import { getEntityLabelKey } from '@/utils/getEntityLabelKey'
import { getSchema } from '@/utils/getSchema'
import { reportError } from '@/utils/reportError'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'

import './FollowUpsSublist.scss'

interface FollowUpsSublistProps {
  originActivity: DataItem
  variant: 'drawer' | 'tab'
  navigateOnCreated?: boolean
  onOpenFollowUp?: (followUp: DataItem) => void
  flashFollowUpId?: string
  initialLimit?: number
}

export const FollowUpsSublist: React.FC<FollowUpsSublistProps> = connect(
  ({
    originActivity,
    variant,
    navigateOnCreated = false,
    onOpenFollowUp: onOpenFollowUpProp,
    flashFollowUpId,
    initialLimit = 5,
  }) => {
    const { t } = useTranslation()
    const originActivityId = originActivity.id()
    const [search, setSearch] = useState('')
    const [limit, setLimit] = useState(initialLimit)
    const { flashId: localFlashId, flash: flashFollowUp } =
      useFlashSublistItem()
    const {
      drawerItem,
      setDrawerItem,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      openDrawerId,
    } = useDataItemDrawer()

    const onSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      setSearch(event.target.value)
    }

    const isSearching = !!search.trim()

    const { schema } = getSchema('activity')
    const schemaAttributes = getLocalizedSchemaAttributes(
      schema?.get('attributes') as SchemaAttributes,
    )
    const types = schemaAttributes?.['type']?.enumValues
    const tabVariant = variant === 'tab'
    const getTypeTitle = (typeValue: string): string => {
      if (!types) return typeValue
      const match = types.find(({ value }) => value === typeValue)
      return match?.title ?? typeValue
    }

    let followUps: DataItem[] = []
    let count = 0
    try {
      const scope = Activity.all().transform({
        filters: { origin: originActivityId },
        limit,
        search: isSearching ? search : undefined,
      })

      followUps = scope.take()
      count = scope.count() ?? 0
    } catch (error) {
      reportError(error)
      followUps = []
    }

    const hasMore = !isSearching
      ? dataScopeHasMore(
          Activity.all(),
          { filters: { origin: originActivityId } },
          limit,
        )
      : false

    const followUpIds = followUps.map((item) => item.id())
    const currentDrawerId = drawerItem?.id()
    const currentIndex = followUpIds.findIndex((id) => currentDrawerId === id)
    const nextId = followUpIds[currentIndex + 1]
    const previousId = followUpIds[currentIndex - 1]

    return (
      <div className="follow-ups-sublist" data-testid="follow-ups-sublist">
        <div className="follow-ups-tab-header my-3">
          <Search
            name="follow-ups-sublist-search"
            searchWrapperClassName={classNames('follow-ups-search', {
              'drawer-search': !tabVariant,
            })}
            onChange={onSearchChange}
            value={search}
            placeholder={t('search.followUpPlaceholder')}
            bgColor={tabVariant ? 'white' : 'default'}
          />
          {tabVariant && (
            <NewFollowUpActivityDrawer
              originActivity={originActivity}
              navigateOnCreated={navigateOnCreated}
              onCreated={(followUpActivity) =>
                flashFollowUp(followUpActivity.id())
              }
            />
          )}
        </div>

        <Sublist>
          {followUps.map((followUp) => (
            <FollowUpSublistItem
              key={followUp.id()}
              followUp={followUp}
              flash={followUp.id() === (flashFollowUpId || localFlashId)}
              getTypeTitle={getTypeTitle}
              onOpenFollowUp={(item: DataItem) => {
                if (onOpenFollowUpProp) {
                  onOpenFollowUpProp(item)
                  return
                }
                setDrawerItem(item)
                openDrawer()
              }}
            />
          ))}

          {!isSearching && (
            <SublistFooter
              amount={followUps.length}
              total={count}
              labelKey={getEntityLabelKey(DataClassName.FOLLOW_UP)}
              onLoadMore={
                hasMore ? () => setLimit((prev) => prev + 10) : undefined
              }
            />
          )}
        </Sublist>

        {!onOpenFollowUpProp && drawerItem && (
          <DataItemDrawer
            key={drawerItem.id()}
            isOpen={isDrawerOpen}
            close={closeDrawer}
            dataItem={drawerItem}
            openDrawerId={openDrawerId}
            nextId={nextId}
            previousId={previousId}
          />
        )}
      </div>
    )
  },
)
